import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

// Permission gating: tabs inside a scoped <Tabs scope="page"> hide when the user lacks "page.value"
const PermCtx = React.createContext(null)

const PermProvider = ({ perms, keys, children }) => (
  <PermCtx.Provider value={{ perms, keys, prefix: null }}>{children}</PermCtx.Provider>
)

function useTabAllowed() {
  const c = React.useContext(PermCtx)
  return (v) => {
    if (!c || !c.prefix || v === undefined) return true
    const k = `${c.prefix}.${v}`
    return !c.keys.has(k) || c.perms.has(k)
  }
}

function useFirstAllowed(ctx) {
  if (!ctx || !ctx.prefix) return undefined
  const depth = ctx.prefix.split('.').length + 1
  const k = [...ctx.keys].find(k => k.startsWith(ctx.prefix + '.') && k.split('.').length === depth && ctx.perms.has(k))
  return k ? k.slice(ctx.prefix.length + 1) : undefined
}

const Tabs = ({ scope, value, defaultValue, onValueChange, ...props }) => {
  const parent = React.useContext(PermCtx)
  const ctx = parent && scope ? { ...parent, prefix: scope } : parent
  const allowedIn = (v) => !ctx || !ctx.prefix || v === undefined || !ctx.keys.has(`${ctx.prefix}.${v}`) || ctx.perms.has(`${ctx.prefix}.${v}`)
  const first = useFirstAllowed(ctx)
  const [inner, setInner] = React.useState(allowedIn(defaultValue) ? defaultValue : first)
  const controlled = value !== undefined
  const current = controlled ? value : inner
  const shown = allowedIn(current) ? current : first
  React.useEffect(() => {
    if (controlled && shown !== value && shown !== undefined && onValueChange) onValueChange(shown)
  }, [controlled, shown, value, onValueChange])
  const handleChange = (v) => { if (!controlled) setInner(v); onValueChange && onValueChange(v) }
  return (
    <PermCtx.Provider value={ctx}>
      <TabsPrimitive.Root value={shown} onValueChange={handleChange} {...props} />
    </PermCtx.Provider>
  )
}

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground",
      className
    )}
    {...props} />
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const allowed = useTabAllowed()
  if (!allowed(props.value)) return null
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
        className
      )}
      {...props} />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => {
  const allowed = useTabAllowed()
  if (!allowed(props.value)) return null
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className
      )}
      {...props} />
  )
})
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent, PermProvider }
